import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router';
import { K } from '../kabinet/atoms';
import { Выпадение } from '../ui/dropdown';
import { инициалы } from '../ui/initials';
import { useTheme } from '../ui/theme-toggle';
import { useSession } from '../auth/session';
import { IconCard, IconChevron, IconExit, IconKey, IconMoon, IconPhone, IconSun, IconTag } from '../ui/icons';

const ProfileAdminEntry = lazy(() => import('./profile-admin-entry'));

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЛИЧНЫЙ ПРОФИЛЬ — КРУЖОК В ШАПКЕ И ВЫПАДЕНИЕ ПОД НИМ.
 *
 * Пакет 25.08, раздел 1. До него всё это стояло в шапке россыпью:
 * переключатель темы отдельным значком, «Пароль и доступ» шестерёнкой,
 * «Выйти» отдельной кнопкой, «Сборка» — вообще пунктом меню разделов.
 * Пять мест на пять действий, которые человек делает раз в месяц.
 *
 * Шапка стала короче на три кнопки, и это не экономия ради экономии:
 * освободившееся место занял выбор кабинета — то, чем пользуются
 * каждый день.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ИМЕНИ ЧЕЛОВЕКА У НАС НЕТ, И ВЫДУМЫВАТЬ ЕГО НЕЛЬЗЯ
 *
 * Эталон рисует две строки: «Анна Ким» и почтой ниже. В нашей базе
 * у пользователя есть только почта — имя при регистрации не спрашивается
 * (ровно та же история, что 24.08 с сотрудником в админке, вопрос 71).
 *
 * Собрать имя из почты — соблазн на две строки кода и враньё у половины
 * адресов: `info@`, `zakupki2@`, `ooo-meduza@`. Поэтому строка одна —
 * почта, а кружок несёт инициалы, собранные из неё же тем самым
 * расчётом, что и в админке.
 *
 * Заведётся поле имени — здесь появится вторая строка и ничего больше.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function Profile() {
  const [открыт, setОткрыт] = useState(false);
  const { session, signOut } = useSession();
  const { dark, toggle } = useTheme();

  const почта = session?.email ?? '';

  // ПОЧТЫ НЕТ — КНОПКИ НЕТ. Кружок с пустыми инициалами и меню без
  // единой подписи выглядят как поломка; их отсутствие — как загрузка
  if (почта === '') return null;

  const закрыть = () => setОткрыт(false);

  return (
    <span className="k-у-выпадения">
      <button
        type="button"
        data-d="кнопка-профиля"
        title="Личный профиль"
        aria-haspopup="menu"
        aria-expanded={открыт}
        /* ОТКРЫТОЕ МЕНЮ ДЕРЖИТ КНОПКУ НАЖАТОЙ. Так в эталоне
           (`--bg-active`), и по делу: выпадение стоит под кнопкой,
           и без подсветки непонятно, чьё оно. Сверка намерила
           подложку на состоянии «меню профиля». */
        className={открыт ? K.кнопкаПрофиляОткрыта : K.кнопкаПрофиля}
        onClick={() => setОткрыт((v) => !v)}
      >
        <span className={K.кружокПрофиля}>{инициалы(почта)}</span>
        {/* КАРЕТКИ НА ТЕЛЕФОНЕ НЕТ — так в эталоне. Там кнопка это
            один кружок с инициалами: 34 точки вместо 50. Каретка
            съедает шестнадцать точек шапки, в которой на 390 и так
            тесно, а сообщает то же, что и сам кружок, — «здесь
            выпадение». Сверка намерила ровно эту разницу. */}
        <span className="rk-only-wide">
          <IconChevron size={12} dir={открыт ? 'up' : 'down'} />
        </span>
      </button>

      <Выпадение открыто={открыт} закрыть={закрыть}>
        <span role="menu" data-d="меню-профиля" className={K.менюПрофиля}>
          <span className={K.шапкаПрофиля}>
            <span className={K.кружокПрофиляКрупный}>{инициалы(почта)}</span>
            <span className={K.почтаПрофиля}>{почта}</span>
          </span>

          <span aria-hidden="true" className={K.чертаПрофиля} />

          <Link
            to="password"
            data-d="профиль-пароль"
            role="menuitem"
            className={`${K.пунктПрофиля} no-underline`}
            onClick={закрыть}
          >
            <span className={K.значокПунктаПрофиля}>
              <IconKey />
            </span>
            Пароль и доступ
          </Link>

          {/* ТЕМА ОСТАЁТСЯ ДЕЙСТВИЕМ, А НЕ ССЫЛКОЙ: она меняет вид
              на месте, и уводить за этим на отдельный экран незачем.
              Надпись называет то, что БУДЕТ, а не то, что есть, —
              так в эталоне: «Тёмная тема» на светлой. */}
          <button
            type="button"
            data-d="профиль-тема"
            role="menuitem"
            className={K.пунктПрофиля}
            onClick={() => {
              toggle();
              закрыть();
            }}
          >
            <span className={K.значокПунктаПрофиля}>{dark ? <IconSun /> : <IconMoon />}</span>
            {dark ? 'Светлая тема' : 'Тёмная тема'}
          </button>

          <Link
            to="plan"
            role="menuitem"
            className={`${K.пунктПрофиля} no-underline`}
            onClick={закрыть}
          >
            <span className={K.значокПунктаПрофиля}>
              <IconCard />
            </span>
            Счета и оплаты
          </Link>

          <Link
            to="pwa"
            data-d="профиль-на-телефон"
            role="menuitem"
            className={`${K.пунктПрофиля} no-underline`}
            onClick={закрыть}
          >
            <span className={K.значокПунктаПрофиля}>
              <IconPhone />
            </span>
            Добавить на телефон
          </Link>

          <Link
            to="/rk/version"
            role="menuitem"
            className={`${K.пунктПрофиля} no-underline`}
            onClick={закрыть}
          >
            <span className={K.значокПунктаПрофиля}>
              <IconTag />
            </span>
            Сборка
          </Link>

          {открыт && (
            <Suspense fallback={null}>
              <ProfileAdminEntry onNavigate={закрыть} />
            </Suspense>
          )}

          <span aria-hidden="true" className={K.чертаПрофиля} />

          <button
            type="button"
            role="menuitem"
            className={K.пунктВыйти}
            onClick={() => void signOut()}
          >
            <span className={K.значокПунктаПрофиля}>
              <IconExit />
            </span>
            Выйти
          </button>
        </span>
      </Выпадение>
    </span>
  );
}
